import { useState } from "react"
import {
    Typography,
    Button,
    Card,
    Switch,
    Select,
    message,
    Modal,
    Form,
    Collapse,
    Tag,
} from "antd"
import {
    PlusOutlined,
    ExclamationCircleOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    SettingOutlined,
} from "@ant-design/icons"

const { Title, Text } = Typography
const { Option } = Select
const { Panel } = Collapse

// Helper for Ant Design message
const showMessage = (type, content) => {
    message[type](content)
}

const Integrations = () => {
    const [integrations, setIntegrations] = useState([
        { id: "asana", name: "Asana", icon: <SettingOutlined />, status: "connected", lastSynced: "2 minutes ago" },
        {
            id: "salesforce",
            name: "Salesforce",
            icon: <SettingOutlined />,
            status: "connected",
            lastSynced: "5 minutes ago",
        },
        { id: "quickbooks", name: "QuickBooks", icon: <SettingOutlined />, status: "error", lastSynced: "45 minutes ago" },
        { id: "slack", name: "Slack", icon: <SettingOutlined />, status: "disconnected", lastSynced: "Never" },
    ])
    const [isAddIntegrationModalVisible, setIsAddIntegrationModalVisible] = useState(false)
    const [addIntegrationForm] = Form.useForm()

    const getStatusTag = (status) => {
        switch (status) {
            case "connected":
                return (
                    <Tag color="green">
                        <CheckCircleOutlined /> Connected
                    </Tag>
                )
            case "disconnected":
                return (
                    <Tag color="red">
                        <CloseCircleOutlined /> Disconnected
                    </Tag>
                )
            case "error":
                return (
                    <Tag color="orange">
                        <ExclamationCircleOutlined /> Error
                    </Tag>
                )
            default:
                return <Tag>{status}</Tag>
        }
    }

    const handleAction = (action, id) => {
        showMessage("info", `${action} integration ${id}`)
        // In a real app, this would trigger API calls
    }

    const handleAddIntegration = (values) => {
        const newIntegration = {
            id: values.tool.toLowerCase().replace(/\s/g, ""),
            name: values.tool,
            icon: <SettingOutlined />, // Placeholder icon
            status: "disconnected", // New integrations start as disconnected
            lastSynced: "Never",
        }
        setIntegrations([...integrations, newIntegration])
        setIsAddIntegrationModalVisible(false)
        addIntegrationForm.resetFields()
        showMessage("success", `${values.tool} integration added. Please configure.`)
    }

    return (
        <Card className="mb-6">
            <div className="mb-4">
                <Title level={4}>Integrations</Title>
                <Text type="secondary">Manage your connected platforms.</Text>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {integrations.map((integration) => (
                    <Card key={integration.id} className="flex flex-col">
                        <div className="flex items-center mb-2">
                            {integration.icon}
                            <Text strong className="ml-2">
                                {integration.name}
                            </Text>
                            <span className="ml-auto">{getStatusTag(integration.status)}</span>
                        </div>
                        <Text type="secondary" className="text-sm mb-2">
                            Last Synced: {integration.lastSynced}
                        </Text>
                        <div className="flex gap-2 mt-auto">
                            {integration.status === "disconnected" && (
                                <Button size="small" onClick={() => handleAction("Reconnect", integration.id)}>
                                    Reconnect
                                </Button>
                            )}
                            {integration.status === "error" && (
                                <Button size="small" onClick={() => handleAction("Reconnect", integration.id)}>
                                    Reconnect
                                </Button>
                            )}
                            {integration.status === "connected" && (
                                <Button size="small" onClick={() => handleAction("Configure", integration.id)}>
                                    Configure
                                </Button>
                            )}
                            <Button size="small" danger onClick={() => handleAction("Remove", integration.id)}>
                                Remove
                            </Button>
                        </div>
                        <Collapse ghost className="mt-4">
                            <Panel header="Per-Integration Settings" key="1">
                                <div className="flex items-center justify-between mb-2">
                                    <Text>Enable Auto-Execution from this Integration</Text>
                                    <Switch defaultChecked={true} />
                                </div>
                                <div className="mb-2">
                                    <Text>Data sync frequency:</Text>
                                    <Select defaultValue="hourly" className="w-full mt-1">
                                        <Option value="hourly">Hourly</Option>
                                        <Option value="daily">Daily</Option>
                                        <Option value="realtime">Real-time (if supported)</Option>
                                    </Select>
                                </div>
                                <Button
                                    type="link"
                                    className="p-0"
                                    onClick={() => showMessage("info", `Viewing logs for ${integration.name}`)}
                                >
                                    View Logs
                                </Button>
                            </Panel>
                        </Collapse>
                    </Card>
                ))}
            </div>

            <Button type="dashed" onClick={() => setIsAddIntegrationModalVisible(true)} icon={<PlusOutlined />}>
                Add New Integration
            </Button>

            <Modal
                title="Add New Integration"
                visible={isAddIntegrationModalVisible}
                onCancel={() => setIsAddIntegrationModalVisible(false)}
                footer={null}
            >
                <Form form={addIntegrationForm} onFinish={handleAddIntegration} layout="vertical">
                    <Form.Item name="tool" label="Select Tool" rules={[{ required: true, message: "Please select a tool!" }]}>
                        <Select placeholder="Select a tool">
                            <Option value="Google Sheets">Google Sheets</Option>
                            <Option value="HubSpot">HubSpot</Option>
                            <Option value="Zendesk">Zendesk</Option>
                            <Option value="Trello">Trello</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit">
                            Connect
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    )
}

export default Integrations;